import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Button,
  Table,
  Space,
  Typography,
  message,
  Tag,
  Alert,
  Spin,
  Dropdown,
  Menu,
  Tabs,
  Collapse,
} from "antd";
import {
  DownloadOutlined,
  ReloadOutlined,
  DatabaseOutlined,
  CloudOutlined,
  FolderOutlined,
} from "@ant-design/icons";
import { supabase } from "../lib/supabase";
import { AdminNavBar } from "../components/AdminNavBar";
import styled from "@emotion/styled";

const { Title, Text } = Typography;

const Container = styled.div`
  padding: 24px;
`;

const StatsCard = styled(Card)`
  margin-bottom: 24px;
`;

interface BucketInfo {
  id: string;
  name: string;
  public: boolean;
  folderCount?: number;
  limited?: boolean;
  quickScan?: boolean;
}

interface BucketResponse {
  buckets?: Array<{
    id: string;
    name: string;
    public: boolean;
    stats?: {
      folderCount?: number;
      limited?: boolean;
      quickScan?: boolean;
    };
  }>;
}

interface DatabaseBackup {
  id: string;
  created_at: string;
  status?: string;
  data?: Record<string, unknown>;
}

interface BulkJobStatus {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  totalBuckets: number;
  completed: number;
  failed: number;
  processing: number;
  downloadUrl?: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
  items: Array<{
    bucketName: string;
    status: string;
    filesProcessed: number;
    errorMessage?: string;
    zipPath?: string;
  }>;
}

interface S3BackupFile {
  key: string;
  size: number;
  lastModified: string; // ISO string format
  path: string;
  fileName: string;
  relativePath?: string; // Relative path from bucket prefix
  companyId?: string;
  bookingId?: string;
  containerId?: string;
  documentType?: string;
}

interface S3FileGroup {
  companyId?: string;
  companyName?: string; // Company name from database
  containerId?: string;
  bookings?: {
    bookingId: string;
    files: S3BackupFile[];
    totalSize: number;
    fileCount: number;
  }[];
  documentTypes?: {
    documentType: string;
    files: S3BackupFile[];
    totalSize: number;
    fileCount: number;
  }[];
  files?: S3BackupFile[];
  totalSize: number;
  fileCount: number;
  groupType: "company" | "container" | "flat";
}

interface S3BackupBucket {
  name: string;
  fileCount: number;
  totalSize: number;
  path: string;
  files?: S3BackupFile[];
  groupedFiles?: S3FileGroup[];
}

interface S3BackupEntry {
  date: string;
  timestamp: string;
  type: "storage" | "database";
  buckets: S3BackupBucket[];
  totalSize: number;
  totalFiles: number;
}

interface S3BackupResponse {
  backups: S3BackupEntry[];
  totalBackups: number;
}

const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  return new Date(value).toLocaleString();
};

const getFunctionsBaseUrl = (): string => {
  // Try to get from environment first
  let supabaseUrl =
    (typeof process !== "undefined" && process.env.VITE_SUPABASE_URL) ??
    import.meta.env?.VITE_SUPABASE_URL ??
    "";

  // Fallback to hardcoded URL from your project
  if (!supabaseUrl) {
    supabaseUrl = "https://mqqjepijtavnwamrbihs.supabase.co";
  }

  const { hostname } = new URL(supabaseUrl);
  const projectRef = hostname.split(".")[0];
  return `https://${projectRef}.functions.supabase.co`;
};

const BackupsAdmin: React.FC = () => {
  const [buckets, setBuckets] = useState<BucketInfo[]>([]);
  const [bucketLoading, setBucketLoading] = useState(false);
  const [dbBackups, setDbBackups] = useState<DatabaseBackup[]>([]);
  const [dbLoading, setDbLoading] = useState(false);
  const [downloadingBucket, setDownloadingBucket] = useState<string | null>(
    null
  );
  const [configWarning, setConfigWarning] = useState<string | null>(null);
  const [bulkJob, setBulkJob] = useState<BulkJobStatus | null>(null);
  const [jobPolling, setJobPolling] = useState<NodeJS.Timeout | null>(null);
  const [s3Backups, setS3Backups] = useState<S3BackupEntry[]>([]);
  const [s3Loading, setS3Loading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [expandedBuckets, setExpandedBuckets] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadBuckets();
    loadDatabaseBackups();
    loadS3Backups();
  }, []);

  const getAuthToken = async () => {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error("Failed to fetch auth session:", error);
      return null;
    }
    return data.session?.access_token ?? null;
  };

  const invokeStorageFunction = async <T = unknown>(
    path: string,
    options?: {
      method?: "GET" | "POST";
      body?: Record<string, unknown>;
    }
  ): Promise<T> => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const { data, error } = await supabase.functions.invoke<T>(path, {
      method: options?.method || "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: options?.body,
    });

    if (error) {
      throw error;
    }

    return data as T;
  };

  const loadBuckets = async () => {
    setBucketLoading(true);
    try {
      const query = new URLSearchParams({ includeStats: "true" }).toString();
      const data = await invokeStorageFunction<BucketResponse>(
        `storage-backups?${query}`,
        { method: "GET" }
      );

      const bucketData = data?.buckets ?? [];
      const normalizedBuckets: BucketInfo[] = bucketData.map((bucket) => ({
        id: bucket.id,
        name: bucket.name,
        public: bucket.public,
        folderCount: bucket.stats?.folderCount ?? 0,
        limited: bucket.stats?.limited ?? false,
        quickScan: bucket.stats?.quickScan ?? false,
      }));

      setBuckets(normalizedBuckets);
    } catch (error) {
      console.error("Error loading buckets:", error);
      message.error("Failed to load storage buckets");
    } finally {
      setBucketLoading(false);
    }
  };

  const loadDatabaseBackups = async () => {
    setDbLoading(true);
    setConfigWarning(null);

    try {
      const data = await invokeStorageFunction<{
        backups?: DatabaseBackup[];
        missingConfiguration?: boolean;
        error?: string;
      }>("storage-backups/db", { method: "GET" });

      if (data?.missingConfiguration) {
        setConfigWarning(
          "Supabase management access token or project ref is not configured. Database backups cannot be listed."
        );
        setDbBackups([]);
        return;
      }

      setDbBackups(data?.backups ?? []);
    } catch (error) {
      console.error("Error loading database backups:", error);
      message.error("Failed to load Supabase database backups");
    } finally {
      setDbLoading(false);
    }
  };

  const loadS3Backups = async () => {
    setS3Loading(true);
    try {
      const data = await invokeStorageFunction<S3BackupResponse>(
        "list-s3-backups/list",
        { method: "GET" }
      );
      setS3Backups(data?.backups ?? []);
    } catch (error) {
      console.error("Error loading S3 backups:", error);
      message.error("Failed to load AWS S3 backups");
    } finally {
      setS3Loading(false);
    }
  };

  const loadS3BackupFiles = async (backup: S3BackupEntry, bucket: S3BackupBucket) => {
    // Skip if files are already loaded
    if (bucket.files) {
      return;
    }

    try {
      const data = await invokeStorageFunction<{ 
        files: S3BackupFile[]; 
        groupedFiles?: S3FileGroup[];
        summary?: {
          totalSize: number;
          fileCount: number;
          prefix: string;
        };
      }>(
        `list-s3-backups/files?prefix=${encodeURIComponent(bucket.path)}`,
        { method: "GET" }
      );
      
      // Update the backup entry with files and grouped files
      setS3Backups(prev => prev.map(b => {
        if (b.date === backup.date && b.timestamp === backup.timestamp && b.type === backup.type) {
          const updatedBuckets = b.buckets.map(bk => {
            if (bk.name === bucket.name) {
              const bucketSize = data.summary?.totalSize || data.files.reduce((sum, f) => sum + f.size, 0);
              const bucketFileCount = data.summary?.fileCount || data.files.length;
              
              return { 
                ...bk, 
                files: data.files,
                groupedFiles: data.groupedFiles,
                fileCount: bucketFileCount,
                totalSize: bucketSize,
              };
            }
            return bk;
          });
          
          // Recalculate totals from all buckets that have been loaded
          const totalSize = updatedBuckets.reduce((sum, bk) => sum + bk.totalSize, 0);
          const totalFiles = updatedBuckets.reduce((sum, bk) => sum + bk.fileCount, 0);
          
          return {
            ...b,
            buckets: updatedBuckets,
            totalSize,
            totalFiles,
          };
        }
        return b;
      }));
    } catch (error) {
      console.error("Error loading backup files:", error);
      message.error("Failed to load backup files");
    }
  };

  // Auto-load files when a backup is expanded
  useEffect(() => {
    const loadFilesForExpandedBackups = async () => {
      for (const key of expandedRowKeys) {
        const backup = s3Backups.find(b => `${b.date}_${b.timestamp}_${b.type}` === key);
        if (backup) {
          // Load files for all buckets in this backup that don't have files yet
          for (const bucket of backup.buckets) {
            if (!bucket.files) {
              await loadS3BackupFiles(backup, bucket);
            }
          }
        }
      }
    };

    if (expandedRowKeys.length > 0 && s3Backups.length > 0) {
      loadFilesForExpandedBackups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedRowKeys]);

  const getS3DownloadUrl = async (key: string): Promise<string | null> => {
    try {
      const data = await invokeStorageFunction<{ url: string }>(
        `list-s3-backups/download-url?key=${encodeURIComponent(key)}`,
        { method: "GET" }
      );
      return data.url;
    } catch (error) {
      console.error("Error getting download URL:", error);
      message.error("Failed to generate download URL");
      return null;
    }
  };

  const downloadS3File = async (file: S3BackupFile) => {
    const url = await getS3DownloadUrl(file.key);
    if (url) {
      window.open(url, "_blank");
      message.success("Download started");
    }
  };

  const startJobProcessing = async (jobId: string) => {
    try {
      console.log('Triggering process-job for:', jobId);
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(
        `${getFunctionsBaseUrl()}/storage-backups/process-job/${jobId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to start job processing:', errorText);
      } else {
        console.log('Job processing started successfully');
      }
    } catch (error) {
      console.error('Error starting job processing:', error);
    }
  };

  const startBulkJob = async () => {
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      const bucketNames = buckets
        .map(b => b.name)
        .filter(name => name !== "bulk_download_jobs"); // Exclude internal bucket

      const response = await fetch(
        `${getFunctionsBaseUrl()}/storage-backups/bulk-download`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            buckets: bucketNames,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to start bulk job");
      }

      const jobData = await response.json();
      const jobId = jobData.jobId;

      message.info(`Started bulk download job for ${jobData.totalBuckets} buckets. Processing in background...`);

      // Start polling for job status
      pollJobStatus(jobId);

      return jobId;
    } catch (error) {
      console.error("Failed to start bulk job:", error);
      throw error;
    }
  };

  const pollJobStatus = async (jobId: string) => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(
        `${getFunctionsBaseUrl()}/storage-backups/job-status/${jobId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get job status");
      }

      const statusData: BulkJobStatus = await response.json();
      setBulkJob(statusData);

      if (statusData.status === 'completed') {
        message.success("Bulk download completed! Click download button to get your files.");
        setJobPolling(null);
      } else if (statusData.status === 'failed') {
        message.error(`Bulk download failed: ${statusData.errorMessage}`);
        setJobPolling(null);
        setBulkJob(null);
      } else {
        // If job is created/pending and no buckets are processing, start processing
        if ((statusData.status === 'created' || statusData.status === 'pending') &&
            statusData.processing === 0) {
          console.log('Starting background processing for job:', jobId);
          startJobProcessing(jobId);
        }

        // Continue polling
        const timeout = setTimeout(() => pollJobStatus(jobId), 3000); // Poll every 3 seconds
        setJobPolling(timeout);
      }
    } catch (error) {
      console.error("Error polling job status:", error);
      message.error("Failed to check job status");
      setJobPolling(null);
      setBulkJob(null);
    }
  };

  const downloadBulkJob = async () => {
    if (!bulkJob?.downloadUrl) return;

    try {
      // Redirect to download URL
      window.location.href = bulkJob.downloadUrl;
      message.success("Download started!");
    } catch (error) {
      console.error("Error downloading:", error);
      message.error("Failed to start download");
    }
  };

  const cancelBulkJob = () => {
    if (jobPolling) {
      clearTimeout(jobPolling);
      setJobPolling(null);
    }
    setBulkJob(null);
    message.info("Bulk download cancelled");
  };

  const downloadAllBuckets = async () => {
    try {
      setDownloadingBucket("all");

      if (bulkJob) {
        // If there's already a job
        if (bulkJob.status === 'completed') {
          // Download the completed job
          await downloadBulkJob();
        } else if (bulkJob.status === 'failed') {
          // Start a new job since the previous one failed
          setBulkJob(null);
          await startBulkJob();
        } else {
          // Job is still processing
          message.info("Bulk download is still processing...");
        }
      } else {
        // Start a new job
        await startBulkJob();
      }
    } catch (error) {
      console.error("Bulk download error:", error);
      message.error(`Bulk download failed: ${error.message}`);
    } finally {
      setDownloadingBucket(null);
    }
  };

  const downloadBucketAsZip = async (
    bucketName: string,
    options: { limit?: number; rootOnly?: boolean } = {}
  ) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      setDownloadingBucket(bucketName);
      const mode = options.rootOnly ? "root files only" :
                   options.limit ? `first ${options.limit} files` : "full backup";
      message.info(`Preparing ${mode} download for bucket "${bucketName}"...`);

      const response = await fetch(
        `${getFunctionsBaseUrl()}/storage-backups/download`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            bucket: bucketName,
            ...options
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Edge function error");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // The filename will be set by the server with appropriate suffixes
      const contentDisposition = response.headers.get("content-disposition");
      const filenameMatch = contentDisposition?.match(/filename="([^"]+)"/);
      link.download = filenameMatch ? filenameMatch[1] :
                    `${bucketName}_backup_${new Date().toISOString().split("T")[0]}.zip`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success(`Bucket "${bucketName}" (${mode}) downloaded successfully`);
    } catch (error) {
      console.error("Error downloading bucket:", error);
      message.error(
        error instanceof Error
          ? error.message
          : "Failed to download bucket backup"
      );
    } finally {
      setDownloadingBucket(null);
    }
  };

  const bucketColumns = [
    {
      title: "Bucket Name",
      dataIndex: "name",
      key: "name",
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: "Visibility",
      dataIndex: "public",
      key: "public",
      render: (isPublic: boolean) => (
        <Tag color={isPublic ? "green" : "blue"}>
          {isPublic ? "Public" : "Private"}
        </Tag>
      ),
    },
    {
      title: "Folders",
      dataIndex: "folderCount",
      key: "folderCount",
      render: (count?: number) =>
        typeof count === "number" ? count.toLocaleString() : "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: BucketInfo) => {
        const menuItems = [
          {
            key: "root-only",
            label: "Download Root Files Only (Fast)",
            onClick: () => downloadBucketAsZip(record.name, { rootOnly: true }),
          },
          {
            key: "sample-100",
            label: "Download Sample (100 files)",
            onClick: () => downloadBucketAsZip(record.name, { limit: 100 }),
          },
          {
            key: "sample-500",
            label: "Download Sample (500 files)",
            onClick: () => downloadBucketAsZip(record.name, { limit: 500 }),
          },
          {
            key: "full",
            label: "Download All Files (Slow)",
            onClick: () => downloadBucketAsZip(record.name),
          },
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            disabled={!!downloadingBucket}
            trigger={["click"]}
          >
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={downloadingBucket === record.name}
            >
              Download Options
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const dbColumns = [
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (value: string) => formatDateTime(value),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status?: string) => (
        <Tag color={status === "COMPLETED" ? "green" : "orange"}>
          {status || "Unknown"}
        </Tag>
      ),
    },
  ];

  const totalFolders = useMemo(
    () => buckets.reduce((sum, bucket) => sum + (bucket.folderCount || 0), 0),
    [buckets]
  );

  const s3BackupColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string, record: S3BackupEntry) => (
        <Space direction="vertical" size={0}>
          <Text strong>{date}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.timestamp.replace(/(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/, "$1-$2-$3 $4:$5:$6")}
          </Text>
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type: string) => (
        <Tag color={type === "storage" ? "blue" : "green"}>
          {type === "storage" ? "Storage" : "Database"}
        </Tag>
      ),
    },
    {
      title: "Buckets",
      key: "buckets",
      render: (_: unknown, record: S3BackupEntry) => (
        <Text>{record.buckets.length} bucket{record.buckets.length !== 1 ? "s" : ""}</Text>
      ),
    },
    {
      title: "Files",
      dataIndex: "totalFiles",
      key: "totalFiles",
      render: (count: number) => count === 0 ? "~" : count.toLocaleString(),
    },
    {
      title: "Total Size",
      dataIndex: "totalSize",
      key: "totalSize",
      render: (size: number) => size === 0 ? "~" : formatFileSize(size),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: S3BackupEntry) => {
        const key = `${record.date}_${record.timestamp}_${record.type}`;
        const isExpanded = expandedRowKeys.includes(key);
        return (
          <Button
            onClick={() => {
              if (isExpanded) {
                setExpandedRowKeys(expandedRowKeys.filter(k => k !== key));
              } else {
                setExpandedRowKeys([...expandedRowKeys, key]);
              }
            }}
          >
            {isExpanded ? "Hide" : "Show"} Details
          </Button>
        );
      },
    },
  ];

  return (
    <Container>
      <AdminNavBar />
      <Title level={2}>Backups</Title>

      <Alert
        message="Backups Overview"
        description="Manage Supabase Storage backups and view AWS S3 automated backups."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <StatsCard>
        <Space size="large">
          <div>
            <Text type="secondary">Total Buckets</Text>
            <br />
            <Text strong style={{ fontSize: 24 }}>
              {buckets.length}
            </Text>
          </div>
          <div>
            <Text type="secondary">Total Folders</Text>
            <br />
            <Text strong style={{ fontSize: 24 }}>
              {totalFolders.toLocaleString()}
            </Text>
          </div>
          <div>
            <Text type="secondary">Latest DB Backup</Text>
            <br />
            <Text strong style={{ fontSize: 24 }}>
              {dbBackups.length > 0
                ? formatDateTime(dbBackups[0]?.created_at)
                : "—"}
            </Text>
          </div>
        </Space>
      </StatsCard>

      {bulkJob && (
        <Card
          title="Bulk Download Progress"
          style={{ marginBottom: 24 }}
          extra={
            <Space>
              {bulkJob.status === 'completed' && (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={downloadBulkJob}
                >
                  Download Files
                </Button>
              )}
              <Button
                danger
                onClick={cancelBulkJob}
              >
                Cancel
              </Button>
            </Space>
          }
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>Status: </Text>
              <Tag color={
                bulkJob.status === 'completed' ? 'green' :
                bulkJob.status === 'failed' ? 'red' :
                bulkJob.status === 'processing' ? 'blue' : 'orange'
              }>
                {bulkJob.status.toUpperCase()}
              </Tag>
              <Text style={{ marginLeft: 16 }}>
                {bulkJob.completed}/{bulkJob.totalBuckets} buckets completed
                {bulkJob.failed > 0 && ` (${bulkJob.failed} failed)`}
              </Text>
            </div>

            {bulkJob.status === 'processing' && (
              <div>
                <Text>Processing buckets:</Text>
                <div style={{ marginTop: 8 }}>
                  {bulkJob.items
                    .filter(item => item.status === 'processing')
                    .map(item => (
                      <Tag key={item.bucketName} color="blue" style={{ margin: '2px' }}>
                        {item.bucketName} ({item.filesProcessed} files)
                      </Tag>
                    ))
                  }
                </div>
              </div>
            )}

            {bulkJob.items.filter(item => item.status === 'completed').length > 0 && (
              <div>
                <Text>Completed buckets:</Text>
                <div style={{ marginTop: 8 }}>
                  {bulkJob.items
                    .filter(item => item.status === 'completed')
                    .map(item => (
                      <Tag key={item.bucketName} color="green" style={{ margin: '2px' }}>
                        {item.bucketName} ({item.filesProcessed} files)
                        {item.zipPath && item.zipPath.includes('*') && (
                          <Text type="secondary" style={{ marginLeft: 4 }}>
                            chunked
                          </Text>
                        )}
                      </Tag>
                    ))
                  }
                </div>
              </div>
            )}

            {bulkJob.items.filter(item => item.status === 'failed').length > 0 && (
              <div>
                <Text>Failed buckets:</Text>
                <div style={{ marginTop: 8 }}>
                  {bulkJob.items
                    .filter(item => item.status === 'failed')
                    .map(item => (
                      <Tag key={item.bucketName} color="red" style={{ margin: '2px' }}>
                        {item.bucketName}
                        {item.errorMessage && (
                          <Text type="secondary" style={{ marginLeft: 4 }}>
                            ({item.errorMessage})
                          </Text>
                        )}
                      </Tag>
                    ))
                  }
                </div>
              </div>
            )}

            {bulkJob.errorMessage && (
              <Alert
                message="Error"
                description={bulkJob.errorMessage}
                type="error"
                showIcon
              />
            )}

            <Text type="secondary">
              Started: {formatDateTime(bulkJob.createdAt)} •
              Updated: {formatDateTime(bulkJob.updatedAt)}
            </Text>
          </Space>
        </Card>
      )}

      <Tabs
        defaultActiveKey="supabase"
        items={[
          {
            key: "supabase",
            label: (
              <Space>
                <FolderOutlined />
                Supabase Storage
              </Space>
            ),
            children: (
              <>
                <Card
                  title="Storage Buckets"
                  extra={
                    <Space>
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => downloadAllBuckets()}
                        loading={downloadingBucket !== null}
                        disabled={!!downloadingBucket || !!bulkJob || buckets.length === 0}
                      >
                        {bulkJob ? (
                          bulkJob.status === 'completed' ? 'Download Files' :
                          bulkJob.status === 'failed' ? 'Retry Download' :
                          'Downloading...'
                        ) : 'Download All Buckets'}
                      </Button>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={loadBuckets}
                        loading={bucketLoading}
                      >
                        Refresh
                      </Button>
                    </Space>
                  }
                  style={{ marginBottom: 24 }}
                >
        <Alert
          message="Download Options"
          description={
            <small>
              <strong>Bulk Download:</strong> Download ALL buckets with up to 10,000 files each (can take several minutes) • <strong>Individual:</strong> Root files only (seconds), Sample downloads (minutes), or Full backup (hours)
            </small>
          }
          type="info"
          showIcon={false}
          style={{ marginBottom: 8, padding: "8px 12px" }}
        />
        <Table
          columns={bucketColumns}
          dataSource={buckets}
          rowKey="id"
          loading={bucketLoading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

                <Card
                  title={
                    <Space>
                      <DatabaseOutlined />
                      Supabase Database Backups
                    </Space>
                  }
                  extra={
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={loadDatabaseBackups}
                      loading={dbLoading}
                    >
                      Refresh
                    </Button>
                  }
                >
                  {configWarning && (
                    <Alert
                      type="warning"
                      message={configWarning}
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  {dbLoading ? (
                    <div style={{ textAlign: "center", padding: "24px 0" }}>
                      <Spin />
                    </div>
                  ) : (
                    <Table
                      columns={dbColumns}
                      dataSource={dbBackups}
                      rowKey={(record) => record.id}
                      pagination={{ pageSize: 10 }}
                      locale={{
                        emptyText: configWarning
                          ? "Database backups unavailable until configuration is completed."
                          : "No Supabase backups found.",
                      }}
                    />
                  )}
                </Card>
              </>
            ),
          },
          {
            key: "s3",
            label: (
              <Space>
                <CloudOutlined />
                AWS S3 Backups
              </Space>
            ),
            children: (
              <Card
                title="AWS S3 Automated Backups"
                extra={
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={loadS3Backups}
                    loading={s3Loading}
                  >
                    Refresh
                  </Button>
                }
              >
                {s3Loading ? (
                  <div style={{ textAlign: "center", padding: "24px 0" }}>
                    <Spin />
                  </div>
                ) : s3Backups.length === 0 ? (
                  <Alert
                    message="No backups found"
                    description="AWS S3 backups will appear here once the automated backup workflow runs."
                    type="info"
                    showIcon
                  />
                ) : (
                  <>
                    <Table
                      columns={s3BackupColumns}
                      dataSource={s3Backups}
                      rowKey={(record) => `${record.date}_${record.timestamp}_${record.type}`}
                      pagination={{ pageSize: 10 }}
                      expandable={{
                        expandedRowKeys,
                        onExpand: (expanded, record) => {
                          const key = `${record.date}_${record.timestamp}_${record.type}`;
                          if (expanded) {
                            setExpandedRowKeys([...expandedRowKeys, key]);
                          } else {
                            setExpandedRowKeys(expandedRowKeys.filter(k => k !== key));
                          }
                        },
                        expandedRowRender: (record) => {
                          const backupKey = `${record.date}_${record.timestamp}_${record.type}`;
                          const allFilesLoaded = record.buckets.every(b => b.files);
                          
                          const loadAllFiles = async () => {
                            for (const bucket of record.buckets) {
                              if (!bucket.files) {
                                await loadS3BackupFiles(record, bucket);
                              }
                            }
                          };

                          return (
                            <div style={{ margin: "16px 0" }}>
                              <div style={{ marginBottom: 16, textAlign: "right" }}>
                                <Button
                                  type="primary"
                                  onClick={loadAllFiles}
                                  disabled={allFilesLoaded}
                                >
                                  {allFilesLoaded ? "All Files Loaded" : "Load All Files"}
                                </Button>
                              </div>
                              <Collapse
                                activeKey={Array.from(expandedBuckets).filter(key => key.startsWith(`${backupKey}-`))}
                                onChange={(keys) => {
                                  const newExpanded = new Set(expandedBuckets);
                                  const bucketKeys = record.buckets.map(b => `${backupKey}-${b.name}`);
                                  
                                  // Remove all bucket keys for this backup
                                  bucketKeys.forEach(key => newExpanded.delete(key));
                                  
                                  // Add the newly expanded ones
                                  if (Array.isArray(keys)) {
                                    keys.forEach(key => newExpanded.add(key));
                                  }
                                  
                                  setExpandedBuckets(newExpanded);
                                }}
                                items={record.buckets.map((bucket) => {
                                  const bucketKey = `${backupKey}-${bucket.name}`;
                                  return {
                                    key: bucketKey,
                                    label: (
                                      <Space>
                                        <Text strong>{bucket.name}</Text>
                                        <Tag>{bucket.fileCount === 0 ? "~" : bucket.fileCount} files</Tag>
                                        <Text type="secondary">
                                          {bucket.totalSize === 0 ? "~" : formatFileSize(bucket.totalSize)}
                                        </Text>
                                      </Space>
                                    ),
                                    extra: (
                                      <Button
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          loadS3BackupFiles(record, bucket);
                                        }}
                                      >
                                        {bucket.files ? "Refresh" : "Load"} Files
                                      </Button>
                                    ),
                                    children: (
                                      <div>
                                        {bucket.groupedFiles && bucket.groupedFiles.length > 0 ? (
                                          <Collapse
                                            ghost
                                            items={bucket.groupedFiles.map((group, groupIdx) => {
                                        if (group.groupType === "company" && group.bookings) {
                                          // Company-based grouping: Company → Booking → Files
                                          return {
                                            key: `company-${group.companyId}-${groupIdx}`,
                                            label: (
                                              <Space>
                                                <Text strong>
                                                  {group.companyName || `Company ${group.companyId}`}
                                                  {group.companyId && group.companyId !== "root" && (
                                                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                                                      (ID: {group.companyId})
                                                    </Text>
                                                  )}
                                                </Text>
                                                <Tag>{group.fileCount === 0 ? "~" : group.fileCount} files</Tag>
                                                <Text type="secondary">{group.totalSize === 0 ? "~" : formatFileSize(group.totalSize)}</Text>
                                              </Space>
                                            ),
                                            children: (
                                              <Collapse
                                                ghost
                                                items={group.bookings.map((booking, bookingIdx) => ({
                                                  key: `booking-${booking.bookingId}-${bookingIdx}`,
                                                  label: (
                                                    <Space>
                                                      <Text>Booking ID: {booking.bookingId}</Text>
                                                      <Tag>{booking.fileCount === 0 ? "~" : booking.fileCount} files</Tag>
                                                      <Text type="secondary">{booking.totalSize === 0 ? "~" : formatFileSize(booking.totalSize)}</Text>
                                                    </Space>
                                                  ),
                                                  children: (
                                                    <Table
                                                      size="small"
                                                      dataSource={booking.files}
                                                      rowKey="key"
                                                      pagination={{ pageSize: 10 }}
                                                      columns={[
                                                        {
                                                          title: "File Name",
                                                          dataIndex: "fileName",
                                                          key: "fileName",
                                                          render: (name: string) => <Text style={{ fontFamily: "monospace" }}>{name}</Text>,
                                                        },
                                                        {
                                                          title: "Size",
                                                          dataIndex: "size",
                                                          key: "size",
                                                          render: (size: number) => formatFileSize(size),
                                                        },
                                                        {
                                                          title: "Modified",
                                                          dataIndex: "lastModified",
                                                          key: "lastModified",
                                                          render: (date: string) => formatDateTime(date),
                                                        },
                                                        {
                                                          title: "Actions",
                                                          key: "actions",
                                                          render: (_: unknown, file: S3BackupFile) => (
                                                            <Button
                                                              size="small"
                                                              icon={<DownloadOutlined />}
                                                              onClick={() => downloadS3File(file)}
                                                            >
                                                              Download
                                                            </Button>
                                                          ),
                                                        },
                                                      ]}
                                                    />
                                                  ),
                                                }))}
                                              />
                                            ),
                                          };
                                        } else if (group.groupType === "container" && group.documentTypes) {
                                          // Container-based grouping: Container → Document Type → Files
                                          return {
                                            key: `container-${group.containerId}-${groupIdx}`,
                                            label: (
                                              <Space>
                                                <Text strong>Container ID: {group.containerId}</Text>
                                                <Tag>{group.fileCount === 0 ? "~" : group.fileCount} files</Tag>
                                                <Text type="secondary">{group.totalSize === 0 ? "~" : formatFileSize(group.totalSize)}</Text>
                                              </Space>
                                            ),
                                            children: (
                                              <Collapse
                                                ghost
                                                items={group.documentTypes.map((docType, docIdx) => ({
                                                  key: `doctype-${docType.documentType}-${docIdx}`,
                                                  label: (
                                                    <Space>
                                                      <Text>Type: {docType.documentType}</Text>
                                                      <Tag>{docType.fileCount === 0 ? "~" : docType.fileCount} files</Tag>
                                                      <Text type="secondary">{docType.totalSize === 0 ? "~" : formatFileSize(docType.totalSize)}</Text>
                                                    </Space>
                                                  ),
                                                  children: (
                                                    <Table
                                                      size="small"
                                                      dataSource={docType.files}
                                                      rowKey="key"
                                                      pagination={{ pageSize: 10 }}
                                                      columns={[
                                                        {
                                                          title: "File Name",
                                                          dataIndex: "fileName",
                                                          key: "fileName",
                                                          render: (name: string) => <Text style={{ fontFamily: "monospace" }}>{name}</Text>,
                                                        },
                                                        {
                                                          title: "Size",
                                                          dataIndex: "size",
                                                          key: "size",
                                                          render: (size: number) => formatFileSize(size),
                                                        },
                                                        {
                                                          title: "Modified",
                                                          dataIndex: "lastModified",
                                                          key: "lastModified",
                                                          render: (date: string) => formatDateTime(date),
                                                        },
                                                        {
                                                          title: "Actions",
                                                          key: "actions",
                                                          render: (_: unknown, file: S3BackupFile) => (
                                                            <Button
                                                              size="small"
                                                              icon={<DownloadOutlined />}
                                                              onClick={() => downloadS3File(file)}
                                                            >
                                                              Download
                                                            </Button>
                                                          ),
                                                        },
                                                      ]}
                                                    />
                                                  ),
                                                }))}
                                              />
                                            ),
                                          };
                                        }
                                        return null;
                                            })}
                                          />
                                        ) : bucket.files && bucket.files.length > 0 ? (
                                          <Table
                                            size="small"
                                            columns={[
                                              {
                                                title: "File Path",
                                                dataIndex: "relativePath",
                                                key: "relativePath",
                                                width: "50%",
                                                render: (relativePath: string | undefined, file: S3BackupFile) => {
                                                  // Show the relative path if available, otherwise show fileName
                                                  const displayPath = relativePath || file.fileName;
                                                  // If it contains slashes, show it as a folder structure
                                                  if (displayPath && displayPath.includes("/")) {
                                                    const parts = displayPath.split("/");
                                                    return (
                                                      <Space size={4} wrap>
                                                        {parts.map((part, idx) => (
                                                          <React.Fragment key={idx}>
                                                            {idx < parts.length - 1 ? (
                                                              <Space size={2}>
                                                                <FolderOutlined style={{ color: "#1890ff", fontSize: 12 }} />
                                                                <Text type="secondary" style={{ fontFamily: "monospace" }}>{part}</Text>
                                                              </Space>
                                                            ) : (
                                                              <Text style={{ fontFamily: "monospace" }}>{part}</Text>
                                                            )}
                                                            {idx < parts.length - 1 && <Text type="secondary">/</Text>}
                                                          </React.Fragment>
                                                        ))}
                                                      </Space>
                                                    );
                                                  }
                                                  return <Text style={{ fontFamily: "monospace" }}>{displayPath || file.fileName}</Text>;
                                                },
                                              },
                                              {
                                                title: "Size",
                                                dataIndex: "size",
                                                key: "size",
                                                render: (size: number) => formatFileSize(size),
                                              },
                                              {
                                                title: "Modified",
                                                dataIndex: "lastModified",
                                                key: "lastModified",
                                                render: (date: string) => formatDateTime(date),
                                              },
                                              {
                                                title: "Actions",
                                                key: "actions",
                                                render: (_: unknown, file: S3BackupFile) => (
                                                  <Button
                                                    size="small"
                                                    icon={<DownloadOutlined />}
                                                    onClick={() => downloadS3File(file)}
                                                  >
                                                    Download
                                                  </Button>
                                                ),
                                              },
                                            ]}
                                            dataSource={bucket.files}
                                            rowKey="key"
                                            pagination={{ pageSize: 20 }}
                                          />
                                        ) : (
                                          <Text type="secondary">Click "Load Files" to view individual files</Text>
                                        )}
                                      </div>
                                    ),
                                  };
                                })}
                              />
                            </div>
                          );
                        },
                      }}
                    />
                  </>
                )}
              </Card>
            ),
          },
        ]}
      />
    </Container>
  );
};

export default BackupsAdmin;

